import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router';
import { TabNavigation, WiconsName, IconsLibrary } from '@platform/witems';
import { useCompleteTask, useCompleteLabReview, useReopenTask, useCancelTask, useUpdateTaskPriority, useFetchOpenTasksForPatient, useResumeTask, useOpenWithdrawalRequest } from '../../state/data/task';
import { useFetchCarePeriodByUser } from '../../state/data/carePeriod';
import { useUpdateCarePlan, useFetchCarePlanByUser, useCarePlanByUser } from '../../state/data/carePlan';
import { usePatientRecord, usePatientArchived } from '../../state/data/patient';
import { useRequestAppointment } from '../../state/data/appointment';
import { useCmsSubmissions } from '../../state/data/cms';
import useCallStore from '../../state/application/useCallStore';
import useTimeTrackingStore from '../../state/application/useTimeTrackingStore';
import usePatientFileData from '../../hooks/usePatientFileData';
import useDemoMode from '../../hooks/useDemoMode';
import { getProgramId } from '../../config/program';
import { milestoneReportDates } from '../../logic/cmsWindows';
import { needsOnboardingReview, readAccessSubtrack } from '../../logic/onboardingReview';
import { resolvePatientPhone, resolvePatientEmail } from '../../logic/patientContact';
import useSpruceCall from '../../hooks/useSpruceCall';
import { resolveBirthdate } from '../../logic/patientDemographics';
import {
  applyOptimisticCompletion, applyOptimisticCancellation, completionActorOf, labNoteActorOf,
  applyOptimisticSnooze, applyOptimisticResume, isSnoozed, completedTemplateAt, taskCarePeriodId, clinicCallOf, callCancelCopy,
} from '../../logic/task';
import { cascadeTemplateCodes, relatedPatientTasks, buildCloseNote, buildCascadeCloseNote, completionPollDone, cancellationPollDone } from '../../logic/taskCascade';
import { notifySpecFor } from '../../logic/taskNotify';
import { labNoteOutcomeOf } from '../../logic/labs';
import { buildEditPlan } from './shared/carePlanFormat';
import { resolveAuthorId } from '../../logic/timeTracking';
import { templateCodeOf, ALIGNMENT_BLOCKING_ONB_CODES } from './shared/constants';
import { elationRedirectHref } from '../../logic/elation';
import PatientFileHeader from './PatientFileHeader';
import Media from '../Media';
import OverviewTab from './tabs/OverviewTab';
import CarePlanTab from './tabs/CarePlanTab';
import ContentsTab from './tabs/ContentsTab';
import IntakesTab from './tabs/IntakesTab';
import MedicationTab from './tabs/MedicationTab';
import LabResultsTab from './tabs/LabResultsTab';
import PatientHomeTab from './tabs/PatientHomeTab';
import DevicesTab from './tabs/DevicesTab';
import TimerChip from './shared/TimerChip';
import NextVisitChip from './shared/NextVisitChip';
import RequestVisitModal from '../RequestVisitModal';
import TimeLogModal from './shared/TimeLogModal';
import AIInsightPanel from '../AIInsightPanel';
import ChatPanel from '../ChatPanel';
import CallModal from '../CallModal';
import LabOrderModal from '../LabOrderModal';
import CreateTaskModal from '../CreateTaskModal';
import TaskHistoryModal from '../TaskHistoryModal';
import TaskCalendarModal from '../TaskCalendarModal';
import AlignmentPacketModal from '../AlignmentPacketModal';
import CmsUnalignmentModal from '../CmsUnalignmentModal';
import WithdrawPatientModal from '../WithdrawPatientModal';
import CmsEligibilityModal from '../CmsEligibilityModal';
import CmsReportingModal from '../CmsReportingModal';
import PathwayPresetModal from '../PathwayPresetModal';
import OnboardingReviewModal from '../OnboardingReviewModal';
import PatientInfoModal from '../PatientInfoModal';
import CompleteNotifyModal from '../CompleteNotifyModal';
import SnoozeTaskModal from '@/components/SnoozeTaskModal';
import NotThisPatientModal from '@/components/NotThisPatientModal';
import { WF_RECONCILE_DELAY_MS } from '@/state/data/_shared/wf-race';
import { Constants } from '@/config/constants';
import { canUnalign } from '@/logic/offboarding';
import {
  withdrawalRequestOf, waitForUnaligned, requestCarePeriodId, holdsOnPeriod, periodCanHold, requestedOnLabel,
} from '@/logic/withdrawalRequest';
import WithdrawalHoldChip from './shared/WithdrawalHoldChip';

const UNALIGNED_POLL_MS = 2500;
const UNALIGNED_POLL_MAX = 12;

// TabNavigation defaults iconLibrary to HMICONS; these glyphs live in the Wicons
// font (only names with a .Wicons_<name> rule render), so set WICONS explicitly.
const TABS = [
  { slug: 'overview', label: 'Overview', iconName: WiconsName.NAVIGATION_HOME, iconLibrary: IconsLibrary.WICONS },
  { slug: 'patient-home', label: 'Patient Home', iconName: WiconsName.NAVIGATION_HEART, iconLibrary: IconsLibrary.WICONS },
  { slug: 'devices', label: 'Devices', iconName: WiconsName.NAVIGATION_DEVICES, iconLibrary: IconsLibrary.WICONS },
  { slug: 'care-plan', label: 'Care Plan', iconName: WiconsName.FEATURE_STETHOSCOPE, iconLibrary: IconsLibrary.WICONS },
  { slug: 'intakes', label: 'Intakes', iconName: WiconsName.NAVIGATION_LIST, iconLibrary: IconsLibrary.WICONS },
  { slug: 'medication', label: 'Medication', iconName: WiconsName.FEATURE_PILL, iconLibrary: IconsLibrary.WICONS },
  { slug: 'labs', label: 'Lab Results', iconName: WiconsName.ANATOMICAL_DROP, iconLibrary: IconsLibrary.WICONS },
];
const VALID_TABS = new Set(TABS.map(t => t.slug));

export default function PatientFileShell({ patient: patientProp, userRole, tasks, allTasksForPatient, currentUser, onRefreshTasks, onOpenEngage, onReviewGateResolved, onClose, tasksError = false }) {
  const demoMode = useDemoMode();
  const completeTaskMut = useCompleteTask();
  const completeLabReviewMut = useCompleteLabReview();
  const reopenTaskMut = useReopenTask();
  const cancelTaskMut = useCancelTask();
  const fetchOpenTasksForPatient = useFetchOpenTasksForPatient();
  const updateTaskPriorityMut = useUpdateTaskPriority();
  const updateCarePlanMut = useUpdateCarePlan();
  const requestAppointmentMut = useRequestAppointment();
  // CMS submissions feed the drawer card's reporting-window + in-flight math
  // (shown to both staff roles; under ['cms'] so submit mutations re-derive it via CMS_KEYS.all).
  const cmsSubmissions = useCmsSubmissions(
    (userRole === 'care_coordinator' || userRole === 'clinician') ? patientProp.userid : null);
  const fetchCarePeriodByUser = useFetchCarePeriodByUser();
  const fetchCarePlanByUser = useFetchCarePlanByUser();
  const startCall = useCallStore(s => s.startCall);

  const {
    dataLoading,
    patient,
    carePlan, setCarePlan,
    carePeriod, setCarePeriod,
    measureStates,
    patientDetails,
    insurance, consent, eligibility,
    intakes, intakesTotalCount,
    bundledTasks, setBundledTasks,
    pollTasksUntil,
    refreshBundle,
  } = usePatientFileData({ patient: patientProp, onRefreshTasks });

  // Context-bearing care plan (Tier-1 /care-plans GET — includes context.onboarding_review
  // + access_subtrack + applied_pathways, which the patient-file view omits).
  const carePlanByUser = useCarePlanByUser(patient.userid);

  // Authoritative contact info lives in the patients table (phone NOT NULL),
  // not the Withings program.getpatient demographics — read it for Call/chat.
  const patientRecord = usePatientRecord(patient.userid);

  // ── URL-driven active tab (deep-linkable, survives reload + back/forward) ──
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // 'contents' is a legacy slug — the tab merged into Care Plan as a section.
  const activeTab = rawTab === 'contents' ? 'care-plan' : VALID_TABS.has(rawTab) ? rawTab : 'overview';
  useEffect(() => {
    if (rawTab !== 'contents') return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'care-plan');
      return next;
    }, { replace: true });
  }, [rawTab, setSearchParams]);
  const onTabChange = useCallback((slug) => {
    const target = VALID_TABS.has(slug) ? slug : 'overview';
    // No-op guard — TabNavigation's [startTab] effect fires onChange on every
    // startTab change; without this it would re-write the URL in a loop.
    if (target === activeTab) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (target === 'overview') next.delete('tab'); else next.set('tab', target);
      return next;
    });
  }, [activeTab, setSearchParams]);
  // Reset scroll on tab change. The scroll container is the slide-over panel
  // (or EngageWorkspace's .patient-detail-panel) — NOT .slide-over-body, which
  // has no overflow.
  useEffect(() => {
    (document.querySelector('.slide-over-panel') || document.querySelector('.patient-detail-panel'))?.scrollTo({ top: 0 });
  }, [activeTab]);
  const openCarePlanTab = useCallback(() => onTabChange('care-plan'), [onTabChange]);

  // ?goto=elation → forward to the Elation chart once the server-resolved ehr_patient_id loads;
  // no chart → drop the param and stay. (Keeps the ehr_patient_id out of the analyst digest.)
  const gotoTarget = searchParams.get('goto');
  useEffect(() => {
    if (gotoTarget !== 'elation' || dataLoading) return;
    const href = elationRedirectHref(gotoTarget, patient?.ehr_patient_id);
    if (href) { window.location.assign(href); return; }
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete('goto'); return n; }, { replace: true });
  }, [gotoTarget, dataLoading, patient?.ehr_patient_id, setSearchParams]);

  // Condensed header: the bubble card carries the identity unscrolled; once it
  // scrolls under the top bar, push the identity (+ actions) into the top bar.
  const headerRef = useRef(null);
  const [headerOut, setHeaderOut] = useState(false);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const root = document.querySelector('.slide-over-panel') || document.querySelector('.patient-detail-panel') || null;
    // Offset by the top-bar height: flip once the bubble card scrolls under it.
    const obs = new IntersectionObserver(
      ([entry]) => setHeaderOut(!entry.isIntersecting),
      { root, rootMargin: '-60px 0px 0px 0px', threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // Re-attach once the skeleton clears (the card with headerRef mounts then).
  }, [dataLoading]);

  // ── Care-plan edit draft (clinician). Kept in the shell so the draft survives
  // tab switches; the save is routed through handleSaveCarePlan. ──
  const [editPlan, setEditPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  // Re-seed the draft from carePlan on load + after a pathway-apply refetch
  // (which sets editingPlan=false first), but NOT while an edit is open — a
  // background carePlan refresh mid-edit must not clobber unsaved changes.
  useEffect(() => {
    if (carePlan && userRole === 'clinician' && !editingPlan) setEditPlan(buildEditPlan(carePlan));
  }, [carePlan, userRole, editingPlan]);

  // ── Modals + transient UI state ──
  const [pathwayModalOpen, setPathwayModalOpen] = useState(false);
  const [onbReviewOpen, setOnbReviewOpen] = useState(false);
  // Set when the care team completes the review — flips needsReview off at once so
  // the parent reveals the now-normal file without waiting for the WF36→WF15
  // confirmed_at cascade (scheduleInvalidate refetches carePlanByUser in the
  // background; the next file open reads the durable flag for real).
  const [reviewedOptimistic, setReviewedOptimistic] = useState(false);
  const [callPatient, setCallPatient] = useState(null);
  const [toast, setToast] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [showTaskCalendar, setShowTaskCalendar] = useState(false);
  const [appointmentConfirm, setAppointmentConfirm] = useState(false);
  const [appointmentBusy, setAppointmentBusy] = useState(false);
  const [appointmentError, setAppointmentError] = useState(null);
  const [labOrderTask, setLabOrderTask] = useState(null);
  const [labOrderNotice, setLabOrderNotice] = useState(null);
  const [alignGate, setAlignGate] = useState(null);
  // Close-task confirmation gate. null = closed.
  // { task, related: TaskInstance[], loading, fetchFailed, busy }
  const [closeGate, setCloseGate] = useState(null);
  const [notifyGate, setNotifyGate] = useState(null);
  const [alignPacketOpen, setAlignPacketOpen] = useState(false);
  const [unalignModalOpen, setUnalignModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  // Set when Unalign is opened for a withdrawal request, so CMS's "unaligned" leads back to Withdraw.
  const [unalignOrigin, setUnalignOrigin] = useState(null);
  const shellAliveRef = useRef(true);
  // Set while the shell waits for `unaligned` after CMS accepted: no second $unalign meanwhile.
  const [awaitingUnaligned, setAwaitingUnaligned] = useState(false);
  useEffect(() => {
    shellAliveRef.current = true;
    return () => { shellAliveRef.current = false; };
  }, []);
  const [cmsEligibilityTask, setCmsEligibilityTask] = useState(null);
  const [cmsReportingTask, setCmsReportingTask] = useState(null);
  const [timeLogOpen, setTimeLogOpen] = useState(false);
  const [patientInfoOpen, setPatientInfoOpen] = useState(false);

  // ── Always-on interaction timer (Medicare CPT time) — runs for as long as this
  // shell is mounted for the patient, hidden tab included. Cleanup is
  // unconditional with the captured uid: stop() no-ops if the store moved on,
  // and StrictMode's fake unmount only discards a sub-60s tail. ──
  const startTimer = useTimeTrackingStore((s) => s.start);
  const stopTimer = useTimeTrackingStore((s) => s.stop);
  const setTimerAttribution = useTimeTrackingStore((s) => s.setAttribution);
  const timerAuthorId = resolveAuthorId(currentUser);
  // Read through a ref so start() gets the author on the FIRST call (opt-in
  // authors then never run a single tick) without authorid entering the deps —
  // a re-run here would cleanup-stop the live session and split it in two.
  const timerAuthorIdRef = useRef(timerAuthorId);
  timerAuthorIdRef.current = timerAuthorId;
  useEffect(() => {
    const uid = patient.userid;
    if (uid == null || uid === '') return undefined;
    startTimer(uid, { programid: getProgramId(), authorid: timerAuthorIdRef.current });
    return () => stopTimer(uid);
  }, [patient.userid, startTimer, stopTimer]);
  // authorid can arrive after mount (account fetch) — updated in place, never a
  // restart. Also keyed on patient.userid: an unkeyed patient switch starts a
  // fresh session that must be re-stamped.
  useEffect(() => {
    if (timerAuthorId) setTimerAttribution({ authorid: timerAuthorId });
  }, [timerAuthorId, patient.userid, setTimerAttribution]);

  // Switching patients resets the transient surfaces (the data hook re-fetches on
  // patient change; these are the UI flags it doesn't own).
  useEffect(() => {
    setShowChat(false);
    setShowTaskHistory(false);
    setShowTaskCalendar(false);
    setEditingPlan(false);
    setAppointmentConfirm(false);
    setAppointmentError(null);
    setCloseGate(null);
    setNotifyGate(null);
    setPatientInfoOpen(false);
    // Re-arm the review gate per patient — don't rely on the parent remount.
    setOnbReviewOpen(false);
    setReviewedOptimistic(false);
  }, [patient.userid]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // Escape dismisses the close-task confirm (never mid-flight).
  useEffect(() => {
    if (!closeGate) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCloseGate(g => (g && !g.busy ? null : g)); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeGate]);

  // First-time care-team review gate. The decision needs BOTH the context care-plan
  // GET (carePlanByUser) and the patient-file view (carePeriod + tasks) — both must resolve
  // before the parent reveals drawer-vs-modal, else the drawer flashes then the modal pops.
  const reviewGateResolved = !carePlanByUser.isLoading && !dataLoading;
  const needsReview = !reviewedOptimistic && reviewGateResolved && needsOnboardingReview({
    carePlan: carePlanByUser.data,
    carePeriod,
    tasks: bundledTasks,
  });
  const appliedPathwaysRaw = carePlanByUser.data?.applied_pathways ?? carePlan?.applied_pathways;
  const appliedPathways = Array.isArray(appliedPathwaysRaw) ? appliedPathwaysRaw : [];

  // Both run pre-paint (layout) so the parent's drawer-hide and this modal-open land
  // in the SAME paint — no blank frame on open, and on completion the modal closes
  // and the drawer reveals together (no flash through to the dashboard).
  useLayoutEffect(() => { if (needsReview) setOnbReviewOpen(true); }, [needsReview, patient.userid]);
  // Report the live first-review decision. While true the parent shows ONLY this
  // modal (drawer hidden, no banner); when it flips false (review completed) the
  // parent reveals the now-normal file.
  useLayoutEffect(() => { if (reviewGateResolved) onReviewGateResolved?.(needsReview); }, [reviewGateResolved, needsReview, onReviewGateResolved]);

  const performComplete = useCallback(async (taskId) => {
    const actor = completionActorOf(currentUser, userRole);
    // Template sourced from the bundle (closure), not the polled row — a view change
    // dropping template info from rows must not silently no-op the sibling wait.
    const code = templateCodeOf((bundledTasks || []).find(t => String(t.task_id) === String(taskId)));
    try {
      await completeTaskMut.mutateAsync({ taskId, notes: '', withingsUserId: patient.userid, actor });
      setToast('Task completed');
      // Optimistically reflect it in the Done list NOW — WF17 flips the status async.
      setBundledTasks(prev => applyOptimisticCompletion(prev, taskId, actor, new Date().toISOString()));
      // WF17 event-creates follow-up tasks (order→review+booking, review→visit pair) —
      // completionPollDone waits for the created sibling so the checklist self-refreshes.
      pollTasksUntil(taskId, (t, all) => completionPollDone(code, t, all));
      return true; // TaskCard keeps the checkbox settled only on a real completion.
    } catch (e) {
      setToast('Error: ' + e.message);
    }
    return false;
  }, [completeTaskMut, currentUser, userRole, patient.userid, pollTasksUntil, setBundledTasks, bundledTasks]);

  // Completing some tasks makes WF17 text + email the patient irreversibly —
  // those go through the confirm gate first, everything else stays one-click.
  const handleComplete = useCallback(async (taskId) => {
    const task = (bundledTasks || []).find(t => String(t.task_id) === String(taskId));
    if (task && notifySpecFor(templateCodeOf(task))) {
      setNotifyGate({ task, busy: false });
      return false;
    }
    return performComplete(taskId);
  }, [bundledTasks, performComplete]);

  async function handleNotifyConfirm(labNote) {
    if (!notifyGate || notifyGate.busy) return;
    const taskId = notifyGate.task.task_id;
    const code = templateCodeOf(notifyGate.task);
    setNotifyGate(g => (g ? { ...g, busy: true, error: null, noteOutcome: null } : g));
    // With the care team's note: the result says whether the completion and the note landed.
    if (labNote) {
      const actor = labNoteActorOf(currentUser, userRole);
      let r;
      try {
        r = await completeLabReviewMut.mutateAsync({ taskId, withingsUserId: patient.userid, labNote, actor });
      } catch (e) {
        // Refused before anything was sent (the note failed the module's check).
        setNotifyGate(g => (g ? { ...g, busy: false, error: e.message } : g));
        return;
      }
      if (r.completed) {
        setBundledTasks(prev => applyOptimisticCompletion(prev, taskId, actor, new Date().toISOString()));
        pollTasksUntil(taskId, (t, all) => completionPollDone(code, t, all));
      } else if (r.unconfirmed) {
        // Possibly still on its way: keep watching, so the file shows Done when it lands.
        pollTasksUntil(taskId, (t, all) => completionPollDone(code, t, all));
      }
      const noteOutcome = labNoteOutcomeOf(r);
      if (!noteOutcome) { setToast('Task completed'); setNotifyGate(null); return; }
      setNotifyGate(g => (g ? { ...g, busy: false, noteOutcome, noteError: r.sendError ?? null } : g));
      return;
    }
    // Kept open on failure, like the task queue: closing would read as success.
    const ok = await performComplete(taskId);
    if (ok) setNotifyGate(null);
    else setNotifyGate(g => (g ? { ...g, busy: false, error: 'The task could not be completed' } : g));
  }

  const handleReopen = useCallback(async (taskId) => {
    // Reopening a CANCELLED task also clears the stale "Closed by …" note; a future
    // available_at goes back to 'scheduled' so the task doesn't surface in the
    // patient companion before its reveal date.
    const row = (bundledTasks || []).find(t => String(t.task_id) === String(taskId));
    const wasCancelled = row?.status === 'cancelled';
    const toStatus = row?.available_at && new Date(row.available_at) > new Date() ? 'scheduled' : 'pending';
    try {
      await reopenTaskMut.mutateAsync({
        taskId, withingsUserId: patient.userid, clearNotes: wasCancelled, toStatus, actor: completionActorOf(currentUser, userRole),
      });
      setToast('Task reopened');
      // Optimistically move it back out of Done (poll reconciles with server truth).
      setBundledTasks(prev => (prev || []).map(t => (String(t.task_id) === String(taskId)
        ? { ...t, status: toStatus, completed_at: null, ...(wasCancelled ? { completion_notes: null } : {}) }
        : t)));
      pollTasksUntil(taskId, (t) => !['completed', 'cancelled', 'missed'].includes(t.status));
    } catch (e) {
      setToast('Error: ' + e.message);
    }
  }, [reopenTaskMut, patient.userid, pollTasksUntil, setBundledTasks, bundledTasks, currentUser, userRole]);

  // Close = cancelled ("no longer needed"), distinct from complete. Opens the confirm
  // gate; mapped templates also list the open patient-side tasks that will auto-close.
  // opts.onClosed: caller-supplied success hook (the Patient Home section feeds its
  // optimistic overlay with it — patient tasks aren't in the staff bundle).
  const resumeTaskMut = useResumeTask();
  // { task, onSnoozed? } — a tab that keeps its own optimistic overlay (Patient Home) passes
  // onSnoozed so the result lands there instead of in the staff bundle.
  const [snoozeTarget, setSnoozeTarget] = useState(null);
  const handleSnooze = useCallback((task, opts = {}) => setSnoozeTarget({ task, onSnoozed: opts.onSnoozed }), []);
  // A clinic-line call taken off this patient: the modal cancels it, the bundle flips now, the poll reconciles.
  const [notThisPatientTask, setNotThisPatientTask] = useState(null);
  const handleCallRemoved = useCallback((task, note) => {
    setBundledTasks(prev => applyOptimisticCancellation(prev, task.task_id, new Date().toISOString(), note));
    setToast(callCancelCopy(clinicCallOf(task)).toast);
    // WF17 refused (or never applied) the cancel: the server's list wins and the coordinator is told.
    pollTasksUntil(task.task_id, t => t.status === 'cancelled', () => {
      refreshBundle().catch(() => null);
      setToast('The call was NOT removed: it still counts for this patient. Try again.');
    });
  }, [setBundledTasks, pollTasksUntil, refreshBundle]);

  // The bundle is LOCAL state, so the mutation's cache patch never reaches it — patch it here
  // and let pollTasksUntil reconcile once WF17 has landed the write (same shape as
  // performComplete). Without this the row only changed on a manual page refresh.
  const handleSnoozed = useCallback((task, until, reason) => {
    const actor = completionActorOf(currentUser, userRole);
    setBundledTasks(prev => applyOptimisticSnooze(prev, task.task_id, until, reason, actor));
    setToast(`Snoozed until ${until}`);
    pollTasksUntil(task.task_id, t => isSnoozed(t));
  }, [currentUser, userRole, setBundledTasks, pollTasksUntil]);

  const handleResume = useCallback(async (task) => {
    try {
      await resumeTaskMut.mutateAsync({
        taskId: task.task_id, withingsUserId: patient.userid, actor: completionActorOf(currentUser, userRole),
      });
      setBundledTasks(prev => applyOptimisticResume(prev, task.task_id));
      setToast('Snooze removed');
      pollTasksUntil(task.task_id, t => !isSnoozed(t));
    } catch (e) {
      setToast('Error: ' + e.message);
    }
  }, [resumeTaskMut, patient.userid, setBundledTasks, pollTasksUntil, currentUser, userRole]);

  const handleCloseRequest = useCallback(async (task, opts = {}) => {
    const code = templateCodeOf(task);
    const mapped = cascadeTemplateCodes(code);
    if (mapped.length === 0) {
      setCloseGate({ task, related: [], loading: false, fetchFailed: false, busy: false, onClosed: opts.onClosed });
      return;
    }
    setCloseGate({ task, related: [], loading: true, fetchFailed: false, busy: false, onClosed: opts.onClosed });
    // Guarded setter: a slow fetch must not clobber a gate reopened for another task.
    const sameTask = (g) => g && !g.busy && String(g.task.task_id) === String(task.task_id);
    try {
      // includeLatent — legacy/in-flight onb_patient_televisit_booking rows sit `scheduled`
      // with a future available_at (new-flow rows are WF17-created available_at=now).
      const patientTasks = await fetchOpenTasksForPatient(patient.userid, { assignedToType: 'patient', includeLatent: true });
      setCloseGate(g => (sameTask(g) ? { ...g, related: relatedPatientTasks(code, patientTasks, taskCarePeriodId(task)), loading: false } : g));
    } catch {
      setCloseGate(g => (sameTask(g) ? { ...g, related: [], loading: false, fetchFailed: true } : g));
    }
  }, [fetchOpenTasksForPatient, patient.userid]);

  async function handleCloseConfirm() {
    if (!closeGate || closeGate.loading || closeGate.busy) return;
    const { task, related } = closeGate;
    setCloseGate(g => (g ? { ...g, busy: true } : g));
    const actor = completionActorOf(currentUser, userRole);
    const note = buildCloseNote(actor);
    try {
      await cancelTaskMut.mutateAsync({ taskId: task.task_id, notes: note, withingsUserId: patient.userid, actor });
    } catch (e) {
      setToast('Error: ' + e.message);
      setCloseGate(g => (g ? { ...g, busy: false } : g));
      return;
    }
    // Staff-bundle tasks flip NOW (the bundle is local state — the mutation's cache patch
    // doesn't reach it) and get the reconcile poll. A PATIENT task (closed from the Patient
    // Home section) isn't in the bundle — its refresh rides the mutation's patientHome
    // invalidation instead.
    const inBundle = (bundledTasks || []).some(t => String(t.task_id) === String(task.task_id));
    if (inBundle) setBundledTasks(prev => applyOptimisticCancellation(prev, task.task_id, new Date().toISOString(), note));
    closeGate.onClosed?.(task.task_id);
    // Cascade best-effort — the staff close stands even if a patient close fails.
    const results = await Promise.allSettled(related.map(rt => cancelTaskMut.mutateAsync({
      taskId: rt.task_id, notes: buildCascadeCloseNote(task.title), withingsUserId: patient.userid, actor,
    })));
    const failed = results.filter(r => r.status === 'rejected');
    failed.forEach(r => console.warn('close-task cascade failed', r.reason));
    setCloseGate(null);
    if (failed.length > 0) setToast(`Task cancelled — could not close ${failed.length} related patient task(s)`);
    else if (related.length > 0) setToast(`Task cancelled — ${related.length} related patient task(s) also closed`);
    else setToast('Task cancelled');
    // A labs-review cancel makes WF17 open the Virtual-Visit pair (same as completing
    // it) — cancellationPollDone waits for the staff one so the checklist self-refreshes.
    if (inBundle) pollTasksUntil(task.task_id, (t, all) => cancellationPollDone(templateCodeOf(task), t, all));
  }

  const handleAlignCms = useCallback(() => {
    const pool = (bundledTasks && bundledTasks.length > 0)
      ? bundledTasks
      : (allTasksForPatient && allTasksForPatient.length > 0)
        ? allTasksForPatient
        : (tasks || []);
    const blocking = pool.filter(t =>
      ALIGNMENT_BLOCKING_ONB_CODES.has(templateCodeOf(t)) && ['pending', 'in_progress', 'overdue'].includes(t.status));
    if (blocking.length > 0) setAlignGate({ blocking });
    else setAlignPacketOpen(true);
  }, [bundledTasks, allTasksForPatient, tasks]);

  // Deep-link intent: /patient/:id?align=1 (from the TaskQueue align action) opens
  // the alignment packet once — after task data loads so the blocking gate is accurate —
  // then strips the param so a reload/back doesn't re-open it.
  const alignIntentConsumed = useRef(false);
  useEffect(() => {
    if (alignIntentConsumed.current || dataLoading) return;
    if (searchParams.get('align') !== '1') return;
    alignIntentConsumed.current = true;
    handleAlignCms();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('align');
      return next;
    }, { replace: true });
  }, [dataLoading, searchParams, handleAlignCms, setSearchParams]);

  async function handleMakeBlockingUrgent() {
    if (!alignGate) return;
    try {
      await Promise.all(alignGate.blocking.map(t => updateTaskPriorityMut.mutateAsync({ taskId: t.task_id, priority: 'urgent', withingsUserId: patient.userid })));
      setToast('Blocking tasks bumped to urgent');
      setAlignGate(null);
    } catch (e) {
      setToast('Error: ' + e.message);
    }
  }

  async function handleAlignmentSubmitted() {
    setAlignPacketOpen(false);
    setToast('CMS alignment submitted');
    onRefreshTasks?.();
    try { const cp = await fetchCarePeriodByUser(patient.userid); setCarePeriod(cp); } catch { /* ignore */ }
  }

  // refreshBundle, not fetchCarePeriodByUser: that read excludes `unaligned`, the very status this lands.
  async function handleUnalignmentSubmitted() {
    setUnalignModalOpen(false);
    setUnalignOrigin(null);
    setToast('CMS unalignment submitted');
    onRefreshTasks?.();
    try { await refreshBundle(); } catch { /* ignore */ }
  }

  // WF24 writes CMS's verdict before the care period, so wait for `unaligned` before re-opening
  // Withdraw: a stale `active` would offer the unalignment a second time.
  async function handleUnalignedFromRequest() {
    setUnalignModalOpen(false);
    setUnalignOrigin(null);
    if (awaitingUnaligned) return;
    setAwaitingUnaligned(true);
    setToast('CMS confirmed the unalignment. Updating the care period…');
    onRefreshTasks?.();
    try {
      const outcome = await waitForUnaligned({
        refresh: refreshBundle, isAlive: () => shellAliveRef.current, pollMs: UNALIGNED_POLL_MS, max: UNALIGNED_POLL_MAX,
      });
      if (outcome === 'unaligned') {
        // Another window opened meanwhile: leave it alone and say where to finish.
        if (document.querySelector('.modal-overlay')) setToast('CMS unalignment confirmed. Open Withdraw to finish.');
        else setWithdrawModalOpen(true);
      } else if (outcome === 'timeout') {
        setToast('CMS confirmed the unalignment; the care period hasn’t updated yet. Reopen Withdraw in a minute.');
      }
    } finally {
      if (shellAliveRef.current) setAwaitingUnaligned(false);
    }
  }

  // refreshBundle, not fetchCarePeriodByUser: that read filters to NON_TERMINAL_STATUSES,
  // so it returns null for a withdrawn period and would blank the CMS card. The
  // patient-file view is unfiltered, so the Withdrawn badge renders.
  async function handleWithdrawSubmitted() {
    setWithdrawModalOpen(false);
    setToast('Patient withdrawn');
    onRefreshTasks?.();
    setTimeout(() => { refreshBundle(); }, WF_RECONCILE_DELAY_MS);
  }

  const handleOrderLab = useCallback((task) => { setLabOrderTask(task); }, []);
  const handleLabOrderSuccess = useCallback((result) => {
    setLabOrderTask(null);
    setLabOrderNotice({ ref: result?.requisition_number || result?.elation_order_id || result?.lab_order_id || null });
    onRefreshTasks?.();
  }, [onRefreshTasks]);

  const handleRunCmsEligibility = useCallback((task) => { setCmsEligibilityTask(task); }, []);
  const handleRunCmsReporting = useCallback((task, reportKind) => { setCmsReportingTask({ ...task, report_kind: reportKind }); }, []);


  // The event door does not return WF33's verdict (a card added, or blocked by an open one), so the toast
  // claims only that the request left.
  async function handleConfirmAppointment(visitKind) {
    if (appointmentBusy) return;
    setAppointmentBusy(true);
    setAppointmentError(null);
    try {
      await requestAppointmentMut.mutateAsync({
        withingsUserId: patient.userid,
        opts: {
          visitKind,
          sendSms: true,
          sentBy: currentUser?.accountid ? String(currentUser.accountid) : undefined,
          sentByFirstname: currentUser?.firstname || currentUser?.shortname || undefined,
          sentByLastname: currentUser?.lastname || undefined,
        },
      });
      setToast('Request sent');
      setAppointmentConfirm(false);
      onRefreshTasks?.();
    } catch (e) {
      setAppointmentError(e.message || 'Could not request the visit');
    } finally {
      setAppointmentBusy(false);
    }
  }

  async function handleSaveCarePlan() {
    if (!editPlan) return;
    setPlanSaving(true);
    try {
      const approverName = currentUser
        ? [currentUser.firstname, currentUser.lastname].filter(Boolean).join(' ') || 'MD'
        : 'MD';
      const cleanedMeds = (editPlan.medications || [])
        .filter(m => m && m.name && String(m.name).trim())
        .map(m => {
          const out = { name: String(m.name).trim(), dose: (m.dose || '').trim(), frequency: (m.frequency || '').trim() };
          if (m.class) out.class = m.class;
          return out;
        });
      const approvalPayload = {
        ...editPlan,
        medications: cleanedMeds,
        status: 'active',
        approved_by: approverName,
        approved_at: new Date().toISOString(),
      };
      await updateCarePlanMut.mutateAsync({ withingsUserId: patient.userid, updates: approvalPayload });
      setCarePlan(prev => ({ ...prev, ...approvalPayload }));
      setToast('Care plan approved & saved');
      // A conditions edit makes WF15 re-resolve subtrack + track (+ care_period.track_code) —
      // refetch plan AND period after the cascade so Align never reads a stale pair.
      setTimeout(async () => {
        try {
          const fresh = await fetchCarePlanByUser(patient.userid);
          if (fresh) setCarePlan(fresh);
          const cp = await fetchCarePeriodByUser(patient.userid);
          if (cp) setCarePeriod(cp);
        } catch { /* best-effort refresh */ }
      }, 2500);
    } catch (e) {
      setToast('Error saving care plan');
    } finally {
      setPlanSaving(false);
    }
  }

  // Unalign lives in "See more" → Offboarding now; the gate is the one the Status card carried.
  const unalignGate = canUnalign(carePeriod, Constants.ENV);
  const anyOpenRequest = useOpenWithdrawalRequest(patient.userid, carePeriod?.care_period_id, {
    enabled: periodCanHold(carePeriod),
  }).data ?? null;
  const holding = holdsOnPeriod(anyOpenRequest, carePeriod);
  const holdArchived = usePatientArchived(holding ? patient.userid : null).data;
  const hold = holding
    ? { requestedOn: requestedOnLabel(anyOpenRequest), chartsLive: holdArchived === false, onOpen: () => setWithdrawModalOpen(true) }
    : null;
  // The unalignment prefills only from this period's request: one on another period is not this unalignment's reason.
  const openWithdrawalRequest = anyOpenRequest && requestCarePeriodId(anyOpenRequest) === String(carePeriod?.care_period_id ?? '')
    ? anyOpenRequest : null;
  // Only a string is a request's reason: a click handler passes its event here.
  function openUnalign(requestReasonLabel) {
    setUnalignOrigin(typeof requestReasonLabel === 'string' ? { from: 'withdraw', requestReasonLabel } : null);
    setUnalignModalOpen(true);
  }

  const phone = resolvePatientPhone(patientRecord.data, patientDetails, patient);
  const openTwilioCall = useCallback(() => setCallPatient({ ...patient, phone_number: phone }), [patient, phone]);
  const call = useSpruceCall(phone, patientRecord.isLoading, openTwilioCall);
  const email = resolvePatientEmail(patientRecord.data, patientDetails, patient);
  const birthdate = resolveBirthdate(patient, patientDetails, insurance);
  // Track lives on the care period (fallback to the plan); subtrack rides the
  // context-bearing plan GET (the patient-file bundle omits context).
  const trackCode = carePeriod?.track_code || carePlan?.track || null;
  const subtrackId = readAccessSubtrack(carePlanByUser.data)?.subtrack_id
    || readAccessSubtrack(carePlan)?.subtrack_id || null;

  if (dataLoading) {
    return (
      <div className="patient-detail-content">
        <div className="detail-header" style={{ flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="skeleton-bar" style={{ width: 180, height: 20, borderRadius: 8 }} />
            <div className="skeleton-bar" style={{ width: 80, height: 14 }} />
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <div><div className="skeleton-bar" style={{ width: 50, height: 10, marginBottom: 6 }} /><div className="skeleton-bar" style={{ width: 110, height: 16 }} /></div>
            <div><div className="skeleton-bar" style={{ width: 70, height: 10, marginBottom: 6 }} /><div className="skeleton-bar" style={{ width: 90, height: 16 }} /></div>
          </div>
          <div className="skeleton-bar" style={{ width: '100%', height: 3, marginTop: 8 }} />
        </div>
        <div style={{ margin: '24px 0 10px' }}><div className="skeleton-bar" style={{ width: 60, height: 12 }} /></div>
        <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 8, overflow: 'hidden' }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ padding: '14px 16px', borderBottom: '1px solid var(--wi-divider)', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="skeleton-circle" style={{ width: 22, height: 22 }} />
              <div style={{ flex: 1 }}>
                <div className="skeleton-bar" style={{ width: `${50 + i * 12}%`, height: 14, marginBottom: 6 }} />
                <div className="skeleton-bar" style={{ width: `${30 + i * 8}%`, height: 10 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="patient-detail-content">
      {/* ── Top bar: just Back until the bubble card scrolls out, then the identity
          (+ actions) is pushed up here. ── */}
      <div className={`patient-file-topbar${headerOut ? ' is-condensed' : ''}`}>
        {onClose && (
          <button type="button" className="patient-file-back" onClick={onClose} aria-label="Back to list" title="Back to patient list">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            {!headerOut && <span className="patient-file-back__label">Back to patient list</span>}
          </button>
        )}
        {headerOut && (
          <>
            <Media size={26} fallback={(patient.firstname?.[0] || '') + (patient.lastname?.[0] || '')} alt="" />
            <div className="patient-file-topbar__id">
              <span className={`patient-file-topbar__name${demoMode ? ' demo-blur' : ''}`}>{patient.firstname} {patient.lastname}</span>
            </div>
          </>
        )}
        <div className="patient-file-topbar__right">
          {headerOut && hold && <WithdrawalHoldChip requestedOn={hold.requestedOn} chartsLive={hold.chartsLive} onOpen={hold.onOpen} />}
          <NextVisitChip tasks={bundledTasks} compact={headerOut} />
          <TimerChip userid={patient.userid} onOpen={() => setTimeLogOpen(true)} />
          {headerOut && (
            <div className="patient-file-topbar__actions">
              <button type="button" className="pf-topbar-action" onClick={() => setPatientInfoOpen(true)} title="Patient contact & administrative details">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                Info
              </button>
              <button type="button" className="pf-topbar-action" onClick={() => onOpenEngage ? onOpenEngage() : setShowChat(c => !c)} title="Chat with patient">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                Chat
              </button>
              <button type="button" className="pf-topbar-action" onClick={call.onCall} disabled={call.pending} aria-busy={call.pending} title={call.label} aria-label={call.label}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                Call
              </button>
              <button type="button" className="pf-topbar-action" disabled={!carePeriod || appointmentBusy} title={!carePeriod ? 'No active care period' : 'Send the patient an Elation booking link'} onClick={() => setAppointmentConfirm(true)}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                Schedule
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── The "bubble" card: identity + actions + contact + care period (scrolls away) ── */}
      <div className="detail-header" ref={headerRef}>
        <PatientFileHeader
          patient={patient}
          carePeriod={carePeriod}
          milestoneDates={milestoneReportDates(cmsSubmissions.data)}
          taskMilestoneDates={(() => {
            const pool = (bundledTasks?.length ? bundledTasks : null) || (allTasksForPatient?.length ? allTasksForPatient : null) || tasks || [];
            return {
              labs: completedTemplateAt(pool, 'onb_baseline_labs_review', carePeriod?.care_period_id),
              visit: completedTemplateAt(pool, 'onb_virtual_visit', carePeriod?.care_period_id),
              ship: completedTemplateAt(pool, 'onb_device_ship', carePeriod?.care_period_id),
              pcp: completedTemplateAt(pool, 'onb_care_plan_share', carePeriod?.care_period_id),
            };
          })()}
          trackCode={trackCode}
          subtrackId={subtrackId}
          birthdate={birthdate}
          email={email}
          onChat={() => onOpenEngage ? onOpenEngage() : setShowChat(c => !c)}
          onCall={call.onCall}
          callLabel={call.label}
          callPending={call.pending}
          onSchedule={() => setAppointmentConfirm(true)}
          hold={hold}
          onOpenInfo={() => setPatientInfoOpen(true)}
          appointmentBusy={appointmentBusy}
        />


        {/* AI patient briefing */}
        <AIInsightPanel
          mode="patient"
          role={userRole}
          withingsUserId={patient.userid}
          patientName={[patient.firstname, patient.lastname && patient.lastname[0] + '.'].filter(Boolean).join(' ') || `Patient ${patient.userid}`}
        />
      </div>

      {/* ── Chat modal (centered overlay) ── */}
      {showChat && createPortal((
        <div className="chat-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowChat(false); }}>
          <div className="chat-modal">
            <ChatPanel patient={{ ...patient, phone_number: phone }} currentUser={currentUser} onClose={() => setShowChat(false)} autoFocus />
          </div>
        </div>
      ), document.body)}

      {/* ── Tabs (pinned just below the top bar) ── */}
      <div className="patient-file-tabbar">
        <TabNavigation tabs={TABS} startTab={activeTab} onChange={onTabChange} />
      </div>

      {/* ── Active tab content ── */}
      <div className="patient-file-tabpanel">
        {activeTab === 'overview' && (
          <OverviewTab
            patient={patient}
            userRole={userRole}
            currentUser={currentUser}
            tasks={tasks}
            allTasksForPatient={allTasksForPatient}
            tasksError={tasksError}
            bundledTasks={bundledTasks}
            carePeriod={carePeriod}
            carePlan={carePlan}
            cmsSubmissions={cmsSubmissions.data}
            measureStates={measureStates}
            trackCode={trackCode}
            subtrackId={subtrackId}
            onComplete={handleComplete}
            onReopen={handleReopen}
            onCloseTask={handleCloseRequest}
            onSnooze={handleSnooze}
            onResume={handleResume}
            onNotThisPatient={setNotThisPatientTask}
            onOrderLab={handleOrderLab}
            onAlignCms={handleAlignCms}
            onRunCmsEligibility={handleRunCmsEligibility}
            onRunCmsReporting={handleRunCmsReporting}
            onOpenCarePlan={openCarePlanTab}
            onOpenTaskCalendar={() => setShowTaskCalendar(true)}
            onOpenTaskHistory={() => setShowTaskHistory(true)}
            onCreateTask={() => setCreateTaskOpen(true)}
          />
        )}
        {activeTab === 'care-plan' && (
          <>
            <CarePlanTab
              carePlan={carePlan}
              carePlanContext={carePlanByUser.data}
              appliedPathways={appliedPathways}
              userRole={userRole}
              editPlan={editPlan}
              setEditPlan={setEditPlan}
              editingPlan={editingPlan}
              setEditingPlan={setEditingPlan}
              planSaving={planSaving}
              onSaveCarePlan={handleSaveCarePlan}
              onOpenPathway={() => setPathwayModalOpen(true)}
            />
            <div style={{ margin: '28px 0 10px' }}>
              <span className="eyebrow text-secondary">Content Curriculum</span>
            </div>
            <ContentsTab patient={patient} carePeriod={carePeriod} measureStates={measureStates} carePlan={carePlan} />
    </>
        )}
        {activeTab === 'intakes' && (
          <IntakesTab intakes={intakes} intakesTotalCount={intakesTotalCount} withingsUserId={patient?.userid} />
        )}
        {activeTab === 'medication' && (
          <MedicationTab patient={patient} userRole={userRole} carePlan={carePlan} onOpenCarePlan={openCarePlanTab} />
        )}
        {activeTab === 'labs' && (
          <LabResultsTab
            measureStates={measureStates}
            cmsSubmissions={cmsSubmissions.data}
            withingsUserId={patient.userid}
            trackCode={trackCode}
            subtrackId={subtrackId}
          />
        )}
        {activeTab === 'patient-home' && (
          <PatientHomeTab
            patient={patient}
            carePeriod={carePeriod}
            currentUser={currentUser}
            userRole={userRole}
            onCloseTask={handleCloseRequest}
            onSnoozeTask={handleSnooze}
            onToast={setToast}
          />
        )}
        {activeTab === 'devices' && (
          <DevicesTab patient={patient} />
        )}
      </div>

      {/* ── Time log modal (opened from the timer chip) ── */}
      {timeLogOpen && (
        <TimeLogModal
          patient={patient}
          carePeriod={carePeriod}
          currentUser={currentUser}
          onClose={() => setTimeLogOpen(false)}
        />
      )}

      {/* ── Task Calendar / History modals ── */}
      {showTaskCalendar && <TaskCalendarModal patient={patient} onClose={() => setShowTaskCalendar(false)} />}
      {showTaskHistory && <TaskHistoryModal patient={patient} onClose={() => setShowTaskHistory(false)} />}

      {/* ── Create custom task modal ── */}
      {createTaskOpen && (
        <CreateTaskModal
          patient={patient}
          carePeriod={carePeriod}
          currentUser={currentUser}
          userRole={userRole}
          onClose={() => setCreateTaskOpen(false)}
          onSuccess={() => {
            setCreateTaskOpen(false);
            setToast('Task created');
            onRefreshTasks?.();
          }}
        />
      )}

      {/* ── Call modal ── */}
      {callPatient && (
        <CallModal patient={callPatient} onClose={() => setCallPatient(null)} onConfirm={() => { startCall(callPatient); setCallPatient(null); }} />
      )}

      {/* ── Lab order modal (Elation via WF31) ── */}
      {labOrderTask && (
        <LabOrderModal
          task={labOrderTask}
          patient={patient}
          carePlan={carePlan}
          currentUser={currentUser ? { ...currentUser, role: userRole } : { role: userRole }}
          onClose={() => setLabOrderTask(null)}
          onSuccess={handleLabOrderSuccess}
        />
      )}

      {appointmentConfirm && createPortal((
        <RequestVisitModal
          patientName={patient.firstname}
          demoMode={demoMode}
          busy={appointmentBusy}
          error={appointmentError}
          onConfirm={handleConfirmAppointment}
          onClose={() => { if (appointmentBusy) return; setAppointmentConfirm(false); setAppointmentError(null); }}
        />
      ), document.body)}

      {/* ── Persistent "finish order in Elation" notice ── */}
      {snoozeTarget && createPortal((
        <SnoozeTaskModal
          task={snoozeTarget.task}
          withingsUserId={patient.userid}
          actor={completionActorOf(currentUser, userRole)}
          onClose={(res) => {
            const t = snoozeTarget;
            setSnoozeTarget(null);
            if (res?.snoozedUntil && t) (t.onSnoozed || handleSnoozed)(t.task, res.snoozedUntil, res.reason || '');
          }}
        />
      ), document.body)}
      {notThisPatientTask && createPortal((
        <NotThisPatientModal
          task={notThisPatientTask}
          withingsUserId={patient.userid}
          actor={completionActorOf(currentUser, userRole)}
          onClose={(res) => {
            const t = notThisPatientTask;
            setNotThisPatientTask(null);
            if (res?.note && t) handleCallRemoved(t, res.note);
          }}
        />
      ), document.body)}
      {labOrderNotice && createPortal((
        <div
          style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1200, maxWidth: 640, width: 'calc(100% - 48px)', background: 'var(--wi-background-moderate, #FEF3C7)', border: '1px solid #F59E0B', borderRadius: 10, padding: '14px 18px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'flex-start', gap: 12 }}
          role="alert"
        >
          <span style={{ fontSize: 20, lineHeight: 1 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              Draft lab order created in Elation{labOrderNotice.ref ? ` · ${labOrderNotice.ref}` : ''}
            </div>
            <div className="body2" style={{ color: 'var(--wi-text-primary-moderate, #7C2D12)', lineHeight: 1.45 }}>
              Open <b className={demoMode ? 'demo-blur' : undefined}>{patient.first_name || ''} {patient.last_name || ''}</b>&apos;s file in Elation to complete the order: pick tests from the vendor compendium, confirm the Dx codes, then <b>sign &amp; send</b>. The task will stay <i>in progress</i> until results come back.
            </div>
          </div>
          <button onClick={() => setLabOrderNotice(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: 'var(--wi-text-secondary)', padding: 0, marginLeft: 4 }}>×</button>
        </div>
      ), document.body)}

      {/* ── CMS alignment — Stage 1: blocking-task gate ── */}
      {alignGate && createPortal((
        <div onClick={(e) => { if (e.target === e.currentTarget) setAlignGate(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 12, padding: 24, maxWidth: 460, width: '100%', boxShadow: '0 12px 32px rgba(0,0,0,0.18)' }}>
            <h3 className="header4" style={{ margin: '0 0 8px' }}>Blocking onboarding tasks open</h3>
            <p className="body2 text-secondary" style={{ margin: '0 0 14px' }}>
              These onboarding tasks should be completed before CMS alignment is submitted:
            </p>
            <div style={{ background: 'var(--wi-background-moderate)', border: '1px solid #F59E0B', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
              <ul className="detail1" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                {alignGate.blocking.map(t => (<li key={t.task_id}>{t.title}</li>))}
              </ul>
              <button className="btn-secondary" style={{ fontSize: 12, padding: '4px 10px', marginTop: 8 }} onClick={handleMakeBlockingUrgent}>
                Bump these to urgent
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" style={{ fontSize: 13, padding: '8px 16px' }} onClick={() => setAlignGate(null)}>Cancel</button>
              <button className="btn-primary" style={{ fontSize: 13, padding: '8px 16px' }} onClick={() => { setAlignGate(null); setAlignPacketOpen(true); }}>
                Proceed to packet review
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* ── Complete confirm for tasks whose completion messages the patient (WF17) ── */}
      {notifyGate && (
        <CompleteNotifyModal
          key={notifyGate.task.task_id}
          task={notifyGate.task}
          withingsUserId={patient.userid}
          busy={notifyGate.busy}
          error={notifyGate.error}
          noteOutcome={notifyGate.noteOutcome}
          noteError={notifyGate.noteError}
          author={labNoteActorOf(currentUser, userRole)}
          onConfirm={handleNotifyConfirm}
          onDismiss={() => setNotifyGate(null)}
          onRequestClose={() => { const t = notifyGate.task; setNotifyGate(null); handleCloseRequest(t); }}
        />
      )}

      {/* ── Close-task confirm (cancelled = no longer needed; lists cascaded patient tasks) ── */}
      {closeGate && createPortal((
        <div onClick={(e) => { if (e.target === e.currentTarget && !closeGate.busy) setCloseGate(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--wi-background-secondary)', borderRadius: 12, padding: 24, maxWidth: 460, width: '100%', boxShadow: '0 12px 32px rgba(0,0,0,0.18)' }}>
            <h3 className="header4" style={{ margin: '0 0 8px' }}>Cancel this task?</h3>
            <p className="body2 text-secondary" style={{ margin: '0 0 14px' }}>
              <b>{closeGate.task.title}</b> will be marked <b>Cancelled — no longer needed</b>.
              {templateCodeOf(closeGate.task) === 'onb_baseline_labs_order' ? (
                <> The patient will be told they&apos;re <b>all set on labs</b> (email + SMS — no blood draw needed).
                Their Virtual-Visit tasks and the <b>CMS alignment task</b> open once “Review baseline lab results”
                is closed out. Remember to <b>discard the draft lab order in Elation</b> — that part isn&apos;t automated.</>
              ) : templateCodeOf(closeGate.task) === 'onb_baseline_labs_review' ? (
                <> The patient&apos;s <b>Virtual-Visit tasks will open</b> (booking + conduct) and the
                <b> CMS alignment task will open</b> if eligibility has passed — cancelling settles the labs question
                just like completing it, and <b>shares nothing with the patient</b>. If you did review results,
                <b> complete</b> it instead so the chart records who reviewed them and when.</>
              ) : (
                <> It won&apos;t count as completed and no follow-up automation will run.</>
              )}
            </p>
            {closeGate.loading && (
              <p className="detail1 text-secondary" style={{ margin: '0 0 14px' }}>Checking for related patient tasks…</p>
            )}
            {closeGate.fetchFailed && (
              <p className="detail1 text-secondary" style={{ margin: '0 0 14px' }}>
                Couldn&apos;t check for related patient tasks — only this care-team task will be closed.
              </p>
            )}
            {closeGate.related.length > 0 && (
              <div style={{ background: 'var(--wi-background-moderate)', border: '1px solid var(--wi-status-moderate)', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                <p className="detail1" style={{ margin: '0 0 4px', fontWeight: 600, color: 'var(--wi-text-primary-moderate)' }}>
                  These patient-side tasks will also be closed:
                </p>
                <ul className="detail1" style={{ margin: 0, paddingLeft: 18, color: 'var(--wi-text-primary-moderate)' }}>
                  {closeGate.related.map(t => (
                    <li key={t.task_id}>{t.title}{t.status === 'scheduled' ? ' (scheduled)' : ''}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn-secondary" style={{ fontSize: 13, padding: '8px 16px' }} onClick={() => setCloseGate(null)} disabled={closeGate.busy}>Keep task</button>
              <button className="btn-primary" style={{ fontSize: 13, padding: '8px 16px' }} onClick={handleCloseConfirm} disabled={closeGate.loading || closeGate.busy}>
                {closeGate.busy ? 'Cancelling…' : 'Cancel task'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* ── CMS alignment — Stage 2: full Alignment Packet review modal ── */}
      {alignPacketOpen && (
        <AlignmentPacketModal
          patient={patient}
          patientDetails={patientDetails}
          carePlan={carePlan}
          carePeriod={carePeriod}
          insurance={insurance}
          consent={consent}
          eligibility={eligibility}
          currentUser={currentUser}
          userRole={userRole}
          onClose={() => setAlignPacketOpen(false)}
          onSubmitted={handleAlignmentSubmitted}
        />
      )}

      {/* ── CMS unalignment — confirm + fire-and-poll verdict ── */}
      {unalignModalOpen && (
        <CmsUnalignmentModal
          patient={patient}
          carePeriod={carePeriod}
          onClose={() => { setUnalignModalOpen(false); setUnalignOrigin(null); }}
          onSubmitted={handleUnalignmentSubmitted}
          onUnaligned={unalignOrigin ? handleUnalignedFromRequest : null}
          requestReasonLabel={unalignOrigin?.requestReasonLabel ?? null}
        />
      )}

      {/* ── Withdrawal — archive in RPM + record via WF67 (no verdict to poll) ── */}
      {withdrawModalOpen && (
        <WithdrawPatientModal
          patient={patient}
          carePeriod={carePeriod}
          onClose={() => setWithdrawModalOpen(false)}
          onSubmitted={handleWithdrawSubmitted}
          onRequestUnalign={awaitingUnaligned ? null : (requestReasonLabel) => { setWithdrawModalOpen(false); openUnalign(requestReasonLabel); }}
          userRole={userRole}
          actor={completionActorOf(currentUser, userRole)}
          onSaved={() => { refreshBundle().catch(() => null); }}
        />
      )}

      {/* ── CMS eligibility ── */}
      {cmsEligibilityTask && (
        <CmsEligibilityModal
          patient={patient}
          carePeriodId={cmsEligibilityTask.care_period_id || carePeriod?.care_period_id}
          taskId={cmsEligibilityTask.task_id}
          onClose={() => { setCmsEligibilityTask(null); onRefreshTasks?.(); }}
        />
      )}

      {/* ── CMS Reporting submission ── */}
      {cmsReportingTask && (
        <CmsReportingModal
          patient={patient}
          carePeriodId={cmsReportingTask.care_period_id || carePeriod?.care_period_id}
          taskId={cmsReportingTask.task_id}
          reportKind={cmsReportingTask.report_kind}
          onClose={() => { setCmsReportingTask(null); onRefreshTasks?.(); }}
        />
      )}

      {/* ── Care Pathway picker — WF36 resolve/apply ── */}
      {pathwayModalOpen && (
        <PathwayPresetModal
          patient={patient}
          carePlan={carePlan}
          currentUser={currentUser}
          onClose={() => setPathwayModalOpen(false)}
          onSuccess={async (outcome) => {
            // The apply mutation already polled the plan until the WF15 write was
            // confirmed (or gave up at 30s) — refetch immediately, no blind delay.
            const confirmed = outcome?.confirmed !== false;
            setPathwayModalOpen(false);
            setToast(confirmed ? 'Pathway applied — refreshing…' : 'Pathway change submitted — refreshing…');
            // The confirmation poll already returned the fresh plan; refetch only as fallback.
            try {
              const fresh = outcome?.plan ?? await fetchCarePlanByUser(patient.userid);
              if (fresh) setCarePlan(fresh);
            } catch { /* keep the current plan — the closing toast still lands */ }
            // Track lives on care_period.track_code (WF15 PATCHed it) — a plan-only refetch leaves TrackBadge + Align stale (|| masks the fresh plan track).
            try { const cp = await fetchCarePeriodByUser(patient.userid); if (cp) setCarePeriod(cp); } catch { /* ignore */ }
            setToast(confirmed ? 'Pathway applied' : 'Pathway change submitted — not confirmed yet, check again in a moment');
          }}
        />
      )}

      {/* ── First-time care-team onboarding review ── */}
      {onbReviewOpen && (
        <OnboardingReviewModal
          patient={patient}
          carePlan={carePlanByUser.data}
          carePeriod={carePeriod}
          eligibility={eligibility}
          consent={consent}
          insurance={insurance}
          intakes={intakes}
          tasks={bundledTasks}
          currentUser={currentUser}
          userRole={userRole}
          onEligibilityChecked={refreshBundle}
          // Abandon (X / Escape / overlay) — while the review is pending there is no
          // drawer to fall back to, so leave the patient file entirely.
          onClose={() => { setOnbReviewOpen(false); onClose?.(); }}
          onFinished={(outcome) => {
            setReviewedOptimistic(true);
            setOnbReviewOpen(false);
            setToast(outcome?.confirmed === false
              ? 'Onboarding review complete — plan change not confirmed yet, check the care plan in a moment'
              : 'Onboarding review complete — refreshing…');
            onRefreshTasks?.();
          }}
        />
      )}

      {/* ── Patient info (read-only contact + administrative reference) ── */}
      {patientInfoOpen && (
        <PatientInfoModal
          patient={patient}
          patientRecord={patientRecord.data}
          insurance={insurance}
          phone={phone}
          email={email}
          birthdate={birthdate}
          loading={patientRecord.isLoading}
          carePeriod={carePeriod}
          onClose={() => setPatientInfoOpen(false)}
          onWithdraw={() => { setPatientInfoOpen(false); setWithdrawModalOpen(true); }}
          onUnalign={unalignGate.visible && !awaitingUnaligned
            ? () => {
              setPatientInfoOpen(false);
              openUnalign(openWithdrawalRequest ? (withdrawalRequestOf(openWithdrawalRequest)?.reasonLabel ?? null) : null);
            }
            : null}
          unalignIsTestReset={unalignGate.testReset}
        />
      )}

      {/* ── Toast ── */}
      {toast && createPortal((
        <div className="cms-success-toast" style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1100 }}>
          {toast}
        </div>
      ), document.body)}
    </div>
  );
}
